import {SpammyBadge} from '@github-ui/commenting/SpammyBadge'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {MarkdownEditHistoryViewer} from '@github-ui/markdown-edit-history-viewer/MarkdownEditHistoryViewer'
import {MarkdownLastEditedBy} from '@github-ui/markdown-edit-history-viewer/MarkdownLastEditedBy'
import {Box, Label, Link, RelativeTime} from '@primer/react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import type {IssueBodyHeader$key} from './__generated__/IssueBodyHeader.graphql'
import type {IssueBodyHeaderSecondaryFragment$key} from './__generated__/IssueBodyHeaderSecondaryFragment.graphql'
import {IssueBodyHeaderActions, type IssueBodyHeaderActionsProps} from './IssueBodyHeaderActions'
import {IssueBodyHeaderAuthor} from './IssueBodyHeaderAuthor'

export type IssueBodyHeaderProps = {
  additionalActions?: React.ReactNode
  comment: IssueBodyHeader$key
  url?: string
  isPullRequest?: boolean
  actionProps?: IssueBodyHeaderActionsProps
  secondaryKey?: IssueBodyHeaderSecondaryFragment$key
}

const IssueBodyHeaderSecondaryFragment = graphql`
  fragment IssueBodyHeaderSecondaryFragment on Comment {
    ...MarkdownEditHistoryViewer_comment
    ...MarkdownLastEditedBy
    showSpammyBadge
  }
`

export function IssueBodyHeader({
  additionalActions,
  comment,
  url,
  isPullRequest,
  actionProps,
  secondaryKey,
}: IssueBodyHeaderProps) {
  const data = useFragment(
    graphql`
      fragment IssueBodyHeader on Comment {
        ...IssueBodyHeaderActions_comment
        createdAt
        viewerDidAuthor
        author {
          ...IssueBodyHeaderAuthor
          ...IssueBodyHeaderActions
          avatarUrl
          login
        }
      }
    `,
    comment,
  )

  const createdAt = new Date(data.createdAt)
  const secondaryData = useFragment(IssueBodyHeaderSecondaryFragment, secondaryKey) ?? undefined
  const isAvatarRefactorEnabled = isFeatureEnabled('issues_react_avatar_refactor')

  const subText = (
    <>
      <span>{isPullRequest ? 'created ' : 'opened '}</span>
      {url ? (
        <Link href={url} sx={{color: 'fg.muted'}} data-testid="issue-body-header-link">
          <RelativeTime sx={{fontSize: 'inherit'}} date={createdAt}>
            on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
          </RelativeTime>
        </Link>
      ) : (
        <RelativeTime sx={{fontSize: 'inherit'}} date={createdAt}>
          on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
        </RelativeTime>
      )}
      {isAvatarRefactorEnabled && <MarkdownLastEditedBy editInformation={secondaryData} includeSeparator />}
      {/** As Pull Request Issue Body Descriptions show Author label to the right of created at date, we need to render this additional label */}
      {isPullRequest && <Label sx={{ml: 2}}>Author</Label>}
    </>
  )

  return (
    <Box
      sx={{
        backgroundColor: data.viewerDidAuthor ? 'accent.subtle' : 'canvas.subtle',
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottom: '1px solid',
        borderBottomColor: data.viewerDidAuthor ? 'accent.muted' : 'border.muted',
        color: 'fg.muted',
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        fontSize: 1,
        py: 1,
        pr: 1,
        pl: 3,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          minHeight: 'var(--control-small-size, 28px)',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          paddingBottom: !isAvatarRefactorEnabled ? 'var(--base-size-6, 6px)' : 0, // if there is no edit history row, offset the spacing
          display: [
            // xsmall
            'grid',
            // small
            'grid',
            // medium
            'flex',
            // large
            'flex',
          ],
          gridTemplateColumns: [
            // xsmall
            'auto 1fr auto auto auto auto',
            // small
            'auto 1fr auto auto auto',
            // medium: not used
            '',
            // large: not used
            '',
          ],
          gridTemplateAreas: [
            // xsmall
            ` 'avatar     title   title   title     badges      actions'
              'avatar     footer  footer  footer    footer      edits'`,
            // small
            ` 'avatar     title   edits     badges      actions'
              'avatar     footer  footer     footer       footer'`,
            // medium: not used
            ``,
            // large: not used
            ``,
          ],
        }}
      >
        {data.author && (
          <Box
            // className={styles.containedAvatar}
            sx={{
              placeSelf: 'center',
              gridArea: 'avatar',
              display: ['flex', 'flex', 'none', 'none'],
              marginRight: 2,
            }}
          >
            <GitHubAvatar size={24} src={data.author.avatarUrl} alt={`@${data.author.login}`} />
          </Box>
        )}

        <Box
          sx={{
            display: ['contents', 'contents', 'flex', 'flex'],
            minWidth: 0,
            gridColumnStart: 'title',
            gridColumnEnd: 'badges',
            flexWrap: 'wrap',
            alignItems: 'center',
            flexShrink: 1,
            flexBasis: 'auto',
            columnGap: '0.45ch',
            paddingTop: 1,
            paddingBottom: 1,
          }}
        >
          <Box
            sx={{
              gridArea: 'title',
              marginTop: ['0px', '0px', '0px', '0px'],
              alignItems: 'center',
              display: 'flex',
              flexShrink: 1,
              flexBasis: 'auto',
              overflow: 'hidden',
            }}
          >
            <IssueBodyHeaderAuthor author={data.author || null} />
          </Box>
          <Box
            sx={{
              gridArea: 'footer',
              paddingBottom: ['var(--base-size-6, 6px)', 'var(--base-size-8, 8px)', 'unset', 'unset'],
              fontSize: [
                'var(--text-body-size-small, 12px)',
                'var(--text-body-size-small, 12px)',
                'var(--text-body-size-medium, 14px)',
                'var(--text-body-size-medium, 14px)',
              ],
              lineHeight: '1.4',
            }}
          >
            {subText}
          </Box>
        </Box>

        <Box
          sx={{
            display: ['contents', 'contents', 'flex', 'flex'],
            whiteSpace: 'nowrap',
            gridArea: 'actions',
            columnGap: 'var(--base-size-4, 4px)',
            justifyContent: 'flex-end',
            flexShrink: 2,
            flexGrow: 1,
            alignItems: 'flex-start',
          }}
        >
          {isAvatarRefactorEnabled && (
            <Box
              sx={{
                gridArea: 'edits',
                display: 'flex',
                justifyContent: 'flex-end',
                overflow: 'hidden',
                flexGrow: 1,
                alignItems: ['flex-start', 'center', 'center', 'center'],
                ml: 2,
              }}
            >
              <MarkdownEditHistoryViewer editHistory={secondaryData} />
            </Box>
          )}
          <Box
            sx={{
              gridColumnStart: 'badges',
              gridColumnEnd: 'actions',
              display: 'flex',
              columnGap: 'var(--base-size-4, 4px)',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                columnGap: 'var(--base-size-8, 8px)',
                ml: 1,
              }}
            >
              {secondaryData?.showSpammyBadge && <SpammyBadge />}
            </Box>
            <Box
              sx={{
                gridArea: 'actions',
                display: 'flex',
                columnGap: 'var(--base-size-4, 4px)',
              }}
            >
              {additionalActions}
              {actionProps && data.author && (
                <IssueBodyHeaderActions
                  url={actionProps.url}
                  comment={data}
                  issueBodyRef={actionProps.issueBodyRef}
                  onReplySelect={actionProps.onReplySelect}
                  startIssueBodyEdit={actionProps.startIssueBodyEdit}
                  viewerCanUpdate={actionProps.viewerCanUpdate}
                  viewerCanReport={actionProps.viewerCanReport}
                  viewerCanReportToMaintainer={actionProps.viewerCanReportToMaintainer}
                  viewerCanBlockFromOrg={actionProps.viewerCanBlockFromOrg}
                  viewerCanUnblockFromOrg={actionProps.viewerCanUnblockFromOrg}
                  issueId={actionProps.issueId}
                  owner={actionProps.owner}
                  ownerId={actionProps.ownerId}
                  ownerUrl={actionProps.ownerUrl}
                  author={data.author}
                  pendingBlock={actionProps.pendingBlock}
                  pendingUnblock={actionProps.pendingUnblock}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

try{ IssueBodyHeader.displayName ||= 'IssueBodyHeader' } catch {}